/**
 * postsService — a camada fina sobre as Cloud Functions de post.
 *
 * Existe para que nenhuma tela escreva o nome de uma function na mão: o
 * contrato mora aqui, num lugar só. Nada de regra de negócio — contagem,
 * XP e idempotência são do servidor (`functions/social/postsVita.js`).
 */

import { collection, doc } from 'firebase/firestore';

import { db } from '../../firebaseConfig';
import callFirebaseFunction from '../../utils/firebaseFunctionCaller';

export const ESCOPOS = { seguindo: 'following', explorar: 'explore', clube: 'club' };

/**
 * Id do post, gerado no CLIENTE antes do upload.
 *
 * Inverte a ordem antiga (subir foto, depois criar doc) por uma razão de
 * moderação: o arquivo na quarentena se chama pelo id do post, e é assim que
 * o worker liga um ao outro quando o gatilho do Storage acorda. `doc()` sem
 * argumento só sorteia um id — não escreve nada, não custa leitura.
 */
export function novoPostId() {
  return doc(collection(db, 'POSTS')).id;
}

/**
 * Cria o post. Ele nasce EM_ANALISE e só aparece no feed depois que a imagem
 * é aprovada — por isso a resposta traz `status` e `xpPrevisto` (promessa),
 * não `xpEarned` (recibo).
 *
 * `midiaPaths` é o carrossel que o NovoPost acabou de subir; `midiaPath` é a
 * foto única (contrato antigo, mantido para chamada em voo); `fotoUrl` é o
 * atalho do post que nasce de um registro (treino, refeição): a foto já existe
 * em outro prefixo e quem a leva para a quarentena é o servidor.
 *
 * `midiaPaths` FALTAVA AQUI. O NovoPost já mandava o array (é o único caminho
 * de foto tirada na câmera ou escolhida na galeria), esta função só lia o
 * singular, e a callable recebia os três campos nulos — então TODO post com
 * foto nova morria em "Foto obrigatória.". Só publicava quem vinha de um
 * registro que já tinha URL http (check-in de treino), que usa `fotoUrl`.
 */
export function criarPost({
  postId, midiaPath, midiaPaths, fotoUrl, legenda, mencoes, local, tipo,
  registroId, competicaoId,
}) {
  return callFirebaseFunction('createPost', {
    postId: postId || null,
    midiaPath: midiaPath || null,
    midiaPaths: Array.isArray(midiaPaths) && midiaPaths.length ? midiaPaths : null,
    photoUrl: fotoUrl || null,
    caption: legenda || '',
    mentions: mencoes || [],
    location: local || null,
    type: tipo || 'POST',
    registroId: registroId || null,
    competitionId: competicaoId || null,
  });
}

export function editarPost({ postId, legenda, mencoes, local }) {
  return callFirebaseFunction('updatePost', {
    postId,
    caption: legenda || '',
    mentions: mencoes || [],
    location: local || null,
  });
}

export function excluirPost(postId) {
  return callFirebaseFunction('deletePost', { postId });
}

export function lerPost(postId) {
  return callFirebaseFunction('getPost', { postId });
}

export function listarFeed({
  escopo = 'explore', cursor = null, competicaoId, clubeId, gymCode, hashtag, limite,
}) {
  return callFirebaseFunction('listFeed', {
    scope: escopo,
    cursor,
    competitionId: competicaoId || null,
    clubeId: clubeId || null,
    gymCode: gymCode || null,
    hashtag: hashtag || null,
    limit: limite || undefined,
  });
}

/** Sugestões de hashtag por prefixo, com a contagem de posts. */
export function sugerirHashtags({ prefixo = '', limite = 8 } = {}) {
  return callFirebaseFunction('suggestHashtags', { prefix: prefixo, limit: limite });
}

export function listarPostsDoUsuario({ uid, cursor = null, comRepublicacoes = false }) {
  return callFirebaseFunction('listUserPosts', {
    targetUid: uid || null,
    cursor,
    includeReposts: comRepublicacoes,
  });
}

export function alternarCurtida(postId) {
  return callFirebaseFunction('toggleLikePost', { postId });
}

export function alternarRepublicacao(postId) {
  return callFirebaseFunction('toggleRepost', { postId });
}

export function listarComentarios({ postId, cursor = null }) {
  return callFirebaseFunction('listComments', { postId, cursor });
}

export function comentar({ postId, texto, mencoes, respondendoA }) {
  return callFirebaseFunction('createComment', {
    postId,
    text: texto,
    mentions: mencoes || [],
    parentId: respondendoA || null,
  });
}

export function excluirComentario({ postId, comentarioId }) {
  return callFirebaseFunction('deleteComment', { postId, commentId: comentarioId });
}

export function alternarCurtidaDoComentario({ postId, comentarioId }) {
  return callFirebaseFunction('toggleLikeComment', { postId, commentId: comentarioId });
}

/** Salvar/dessalvar um post. O servidor decide o estado — aqui é só o gesto. */
export function alternarSalvar(postId) {
  return callFirebaseFunction('toggleSavePost', { postId });
}

/** Os posts que eu salvei, já resolvidos (post removido some da lista). */
export function listarSalvos() {
  return callFirebaseFunction('listSavedPosts', {});
}

/** Pessoas da mesma academia que eu ainda não sigo. */
export function sugerirPessoas() {
  return callFirebaseFunction('suggestPeople', {});
}

export function seguirUsuario(targetUid) {
  return callFirebaseFunction('followUser', { targetUid });
}
